import React, { Suspense, lazy } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Stack,
  Text,
} from '@eluve/components';

import { usePdfContext } from './PdfContext';

/**
 * pdfjs (~1.3 MB) loads with the first preview rather than at app startup. The
 * boundary lives here rather than at the consumer so callers — including the
 * extension's chat surface — need no Suspense boundary of their own.
 */
const PdfPreviewCanvas = lazy(() =>
  // An undefined module means the chunk fetch failed and chunk-reload-recovery
  // preventDefault()ed `vite:preloadError` with a reload in flight — suspend on
  // the fallback rather than throw a phantom TypeError (see `lazyRoute`).
  import('./PdfPreviewCanvas').then((m) =>
    m ? { default: m.PdfPreviewCanvas } : new Promise<never>(() => undefined),
  ),
);

const LoadingSkeleton = () => (
  <Stack align="center" justify="center" gap={4} className="p-8">
    <Skeleton className="h-[600px] w-[450px]" />
    <Skeleton className="h-4 w-32" />
  </Stack>
);

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <Stack
    align="center"
    justify="center"
    className="p-8"
    gap={2}
    direction="horizontal"
  >
    <Text inline color="primary">
      {message}
    </Text>
  </Stack>
);

export const PdfPreviewDialog: React.FC = () => {
  const {
    showPreview,
    setShowPreview,
    currentPreviewFile,
    currentPage,
    totalPages,
    loading,
    error,
    onDocumentLoadSuccess,
    textRenderer,
    handlePrevPage,
    handleNextPage,
  } = usePdfContext();
  return (
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent size="4xl" className="h-[80vh]">
        <DialogHeader>
          <DialogTitle>{currentPreviewFile?.fileName}</DialogTitle>
        </DialogHeader>

        {/*  Navigation */}
        {totalPages > 1 && (
          <Stack
            gap={3}
            justify="center"
            className="border-b pb-4"
            direction="horizontal"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
            >
              ← Prev
            </Button>
            <Text inline size="xs" color="secondary">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
            >
              Next →
            </Button>
          </Stack>
        )}

        <div className="flex-1 overflow-auto">
          <Stack direction="horizontal" justify="center" align="stretch">
            {loading && <LoadingSkeleton />}

            {error && <ErrorMessage message={error} />}

            {!loading && !error && currentPreviewFile?.fileUrl && (
              <Suspense fallback={<LoadingSkeleton />}>
                <PdfPreviewCanvas
                  fileUrl={currentPreviewFile.fileUrl}
                  currentPage={currentPage}
                  onDocumentLoadSuccess={onDocumentLoadSuccess}
                  textRenderer={textRenderer}
                  loadingSkeleton={<LoadingSkeleton />}
                  renderError={(message) => <ErrorMessage message={message} />}
                />
              </Suspense>
            )}
          </Stack>
        </div>
      </DialogContent>
    </Dialog>
  );
};
